import type {
  RefObject,
  SVGProps,
} from 'react';

import {
  LayoutAssetViewport,
  type EmptyLayoutAssetViewportProps,
  type ImageLayoutAssetViewportProps,
  type PdfLayoutAssetViewportProps,
} from './LayoutAssetViewport';
import {
  LayoutScene,
  type LayoutSceneProps,
} from './LayoutScene';
import type { SvgLayoutDimensions } from './SvgLayoutViewport';

export interface PdfSceneOverlayProps {
  svgRef: RefObject<SVGSVGElement>;
  groupRef: RefObject<SVGGElement>;
  svgProps?: Omit<
    SVGProps<SVGSVGElement>,
    'children' | 'height' | 'ref' | 'viewBox' | 'width'
  >;
  groupProps?: Omit<
    SVGProps<SVGGElement>,
    'children' | 'ref'
  >;
}

type LayoutSceneSource =
  | LayoutSceneProps
  | ((dimensions: SvgLayoutDimensions) => LayoutSceneProps);

interface LayoutAssetSceneViewportCommonProps {
  scene?: LayoutSceneSource;
}

export type LayoutAssetSceneViewportProps =
  | (Omit<ImageLayoutAssetViewportProps, 'children'> &
      LayoutAssetSceneViewportCommonProps)
  | (Omit<PdfLayoutAssetViewportProps, 'children'> &
      LayoutAssetSceneViewportCommonProps & {
        pdfOverlay: PdfSceneOverlayProps;
      })
  | (Omit<EmptyLayoutAssetViewportProps, 'children'> &
      LayoutAssetSceneViewportCommonProps);

const resolveScene = (
  source: LayoutSceneSource | undefined,
  dimensions: SvgLayoutDimensions,
) =>
  typeof source === 'function' ? source(dimensions) : source;

/**
 * Canonical asset and scene composition for both layout formats.
 *
 * Image assets already render children inside their transformed SVG group.
 * PDF assets need a transparent SVG aligned over the shared PDF canvas; this
 * component owns that shell so hosts only provide scene policy and optional
 * overlay events.
 */
export const LayoutAssetSceneViewport = (
  props: LayoutAssetSceneViewportProps,
) => {
  if (props.asset.kind === 'empty') {
    const { scene: _scene, ...viewportProps } = props;
    return <LayoutAssetViewport {...viewportProps} />;
  }

  if (props.asset.kind === 'image') {
    const { scene, ...viewportProps } = props;
    return (
      <LayoutAssetViewport {...viewportProps}>
        {(dimensions) => {
          const sceneProps = resolveScene(scene, dimensions);
          return sceneProps ? <LayoutScene {...sceneProps} /> : null;
        }}
      </LayoutAssetViewport>
    );
  }

  if (!('pdfOverlay' in props)) {
    const { scene: _scene, ...viewportProps } = props;
    return <LayoutAssetViewport {...viewportProps} />;
  }

  const {
    scene,
    pdfOverlay,
    ...viewportProps
  } = props;
  const {
    svgRef,
    groupRef,
    svgProps,
    groupProps,
  } = pdfOverlay;
  const {
    style: svgStyle,
    ...restSvgProps
  } = svgProps ?? {};

  return (
    <LayoutAssetViewport {...viewportProps}>
      {(dimensions) => {
        const sceneProps = resolveScene(scene, dimensions);
        return (
          <svg
            {...restSvgProps}
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            data-layout-overlay
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              overflow: 'visible',
              ...svgStyle,
            }}
          >
            <g
              {...groupProps}
              ref={groupRef}
              data-layout-overlay-root
            >
              {sceneProps ? <LayoutScene {...sceneProps} /> : null}
            </g>
          </svg>
        );
      }}
    </LayoutAssetViewport>
  );
};
